/**
 * signaturePlacements.js
 *
 * Placement rectangles for rendering teacher / principal (and, where the form
 * has one, ECE) signatures onto each report-type PDF template.
 *
 * COORDINATE SPACE
 * ----------------
 * Every rect below is in **pdf-lib space**: bottom-left origin, points (1pt =
 * 1/72"). `(x, y)` is the BOTTOM-LEFT corner; increasing `y` moves UP. This is
 * the same space `page.drawImage()` / `page.drawText()` / `widget.getRectangle()`
 * use, so a rect here can be passed straight through with no conversion. `page`
 * is the 0-based page index.
 *
 * PROVENANCE (`source` on each entry)
 * -----------------------------------
 *  1. "widget"  — the template already has a real AcroForm signature field
 *     (`teacherSignature` / `principalSignature`, a PDFSignature widget). The
 *     rect is that widget's exact rectangle, copied verbatim. DO NOT retune it.
 *
 *  2. "derived" — the template has NO signature AcroForm field. The rect was
 *     placed by hand following PDF_FIELD_FILLING_GUIDE.md. Note from the guide
 *     that matters here: on these particular templates pdfjs's text matrix does
 *     NOT line up with pdf-lib via the usual `y = H - transform[5]` flip — for
 *     the kindergarten forms pdfjs `transform[5]` already reads as a bottom-up
 *     value that matches pdf-lib `y` directly. So rather than trusting a blind
 *     coordinate conversion, each derived box was located by measuring the
 *     printed signature-line anchors (the "X" marker on the 2016 form, the
 *     "...'s Signature" descriptor + its rule on the 2023 form) in pdfjs,
 *     drawing a test rectangle at the candidate coords, rasterizing the page
 *     (pdftoppm) and confirming visually that the box sits ON the signature
 *     line just above its descriptor. Font size matches the printed text
 *     (8-10pt). Boxes start just to the right of the line's left anchor and are
 *     sized to stay inside the cell.
 *
 * Keys are the report-type registry keys (see registry.js). `fontSize` is the
 * suggested size when rendering a typed name in lieu of a drawn signature image.
 */

export const SIGNATURE_PLACEMENTS = {
  // ───────────────────────── widget-backed templates ─────────────────────────
  // Real PDFSignature widgets exist; rectangles copied verbatim — do not change.

  // Fields "teacherSignature" / "principalSignature" on page index 1.
  pgrc1to6_2023_07: {
    teacher: { source: 'widget', page: 1, x: 128.18, y: 269.79, width: 100.0, height: 25.0, fontSize: 8 },
    principal: { source: 'widget', page: 1, x: 410.18, y: 271.12, width: 100.0, height: 25.0, fontSize: 8 },
  },

  // Fields "teacherSignature" / "principalSignature" on page index 1.
  pgrc7to8_2023_07: {
    teacher: { source: 'widget', page: 1, x: 128.4, y: 246.87, width: 161.33, height: 21.67, fontSize: 8 },
    principal: { source: 'widget', page: 1, x: 409.73, y: 246.87, width: 161.33, height: 21.67, fontSize: 8 },
  },

  // Fields "teacherSignature" / "principalSignature" on page index 3.
  pvrc1to6_2023_09: {
    teacher: { source: 'widget', page: 3, x: 112.24, y: 383.02, width: 178.0, height: 37.67, fontSize: 9 },
    principal: { source: 'widget', page: 3, x: 382.24, y: 381.69, width: 178.0, height: 37.67, fontSize: 9 },
  },

  // Fields "teacherSignature" / "principalSignature" on page index 3.
  pvrc7to8_2023_09: {
    teacher: { source: 'widget', page: 3, x: 114.13, y: 352.2, width: 170.0, height: 27.67, fontSize: 8 },
    principal: { source: 'widget', page: 3, x: 388.13, y: 351.53, width: 170.0, height: 27.67, fontSize: 8 },
  },

  // PRINCIPAL ONLY — this graduation-completion form has no teacher signature.
  // Field "principalSignature" (PDFSignature widget) on page index 2, plus its
  // sibling "principalSignatureDate" text widget just to the right. Both rects
  // copied verbatim from the template widgets.
  pvrc9to12_2022_02_sem: {
    principal: { source: 'widget', page: 2, x: 138.17, y: 36.91, width: 197.27, height: 23.18, fontSize: 9 },
    principalDate: { source: 'widget', page: 2, x: 386.45, y: 38.42, width: 135.5, height: 16.8, fontSize: 9 },
  },

  // ─────────────────────── derived (no signature field) ──────────────────────

  // Kindergarten — Initial Observations (3044E, 2016/06). No signature widgets.
  // Printed signature block is on page index 1: a "Teacher's Signature" /
  // "Principal's Signature" pair, each with an "X" marker on the signature line
  // below the descriptor, and an "Early Childhood Educator(s)" line above them.
  // Measured anchors (pdf-lib bottom-origin y):
  //   "X" (teacher)   x≈56.4  y≈138   |  "Teacher's Signature"   label y≈151
  //   "X" (principal) x≈344.8 y≈138   |  "Principal's Signature" label y≈151
  //   "Early Childhood Educator(s)" label x≈103.7 y≈75
  // Boxes start just right of each "X" and sit on the signature line.
  kindergarten_initial_2016_06: {
    teacher: { source: 'derived', page: 1, x: 64, y: 137, width: 270, height: 16, fontSize: 10 },
    principal: { source: 'derived', page: 1, x: 352, y: 137, width: 222, height: 16, fontSize: 10 },
    ece: { source: 'derived', page: 1, x: 236, y: 73, width: 270, height: 14, fontSize: 9 },
  },

  // Kindergarten — Communication of Learning (3046E, 2023/09). No signature
  // widgets. Printed "Teacher's Signature" / "Principal's Signature" descriptors
  // on page index 1, each with a signature rule above the descriptor; near the
  // bottom of the same page an "Early Childhood Educator(s) ____ contributed to
  // the observation," inline blank names the ECE. Measured anchors (pdf-lib
  // bottom-origin y):
  //   "Teacher's Signature"   descriptor x≈55.3  y≈142  (sign on rule just above)
  //   "Principal's Signature" descriptor x≈340.8 y≈142
  //   "Early Childhood Educator(s) ____" run starts x≈118.8 y≈58.6; the blank
  //     itself begins after the ~158pt label, i.e. x≈277.
  // Teacher/principal boxes sit on the rule (y≈154) just above each descriptor;
  // the ECE name drops into the bottom inline blank.
  // Corrected 2026-06-05 (visually verified): the signature rule is BELOW the
  // "...'s Signature" label (label y≈145, underline y≈105). The box sits just
  // ABOVE the underline in the gap toward the label, shifted right of the label.
  kindergarten_final_2023_09: {
    teacher: { source: 'derived', page: 1, x: 115, y: 108, width: 175, height: 24, fontSize: 9 },
    principal: { source: 'derived', page: 1, x: 405, y: 108, width: 175, height: 24, fontSize: 9 },
    ece: { source: 'derived', page: 1, x: 280, y: 56, width: 195, height: 12, fontSize: 9 },
  },
};

export default SIGNATURE_PLACEMENTS;
